import React from 'react';

// ── Sub-components ─────────────────────────────────────────────

export function PlaceholderImage({ label, h, bone, sage }) {
  return (
    <div
      data-testid="placeholder-image"
      style={{
        height: h,
        width: '100%',
        background: `repeating-linear-gradient(135deg, ${sage}22 0 8px, transparent 8px 16px), ${bone}`,
        border: '1px solid ' + sage + '55',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'ui-monospace, SFMono-Regular, monospace',
        fontSize: 11,
        color: sage,
        letterSpacing: 1,
        textTransform: 'uppercase',
      }}
    >
      {label}
    </div>
  );
}

export function BotanicalMark({ color, size }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      aria-hidden="true"
      data-testid="botanical-mark"
    >
      <path
        d="M32 6 C 18 18, 14 36, 32 58 C 50 36, 46 18, 32 6 Z"
        stroke={color}
        strokeWidth="1"
        fill="none"
      />
      <path d="M32 12 L32 54" stroke={color} strokeWidth="0.7" />
      <path d="M32 22 C 26 24, 22 28, 20 32" stroke={color} strokeWidth="0.7" fill="none" />
      <path d="M32 22 C 38 24, 42 28, 44 32" stroke={color} strokeWidth="0.7" fill="none" />
      <path d="M32 32 C 25 35, 20 40, 18 44" stroke={color} strokeWidth="0.7" fill="none" />
      <path d="M32 32 C 39 35, 44 40, 46 44" stroke={color} strokeWidth="0.7" fill="none" />
    </svg>
  );
}

// ── Plant data (source of truth) ────────────────────────────────

export const PLANTS = [
  {
    name: 'Taiwan',
    age: '2 yr',
    height: '12–16″',
    price: 25,
    note: 'Oolong heritage; vigorous, glossy leaf.',
  },
  {
    name: 'Korea #1',
    age: '2 yr',
    height: '18–22″',
    price: 25,
    note: 'Cold-hardy, upright habit.',
  },
  { name: 'Korea #2', age: '2 yr', height: '12–16″', price: 25, note: 'Sister to #1, finer leaf.' },
  {
    name: 'Black Sea',
    age: '2 yr',
    height: '20–24″',
    price: 25,
    note: 'From Georgia stock; very hardy.',
  },
  {
    name: 'Sochi',
    age: '2 yr',
    height: '10–14″',
    price: 25,
    note: 'Russian black-tea provenance.',
  },
  {
    name: 'Yabukita',
    age: '2 yr',
    height: '6–10″',
    price: 25,
    note: 'The classic Japanese cultivar.',
  },
  { name: 'China #1', age: '2 yr', height: '12–16″', price: 25, note: 'Small-leaf var. sinensis.' },
  { name: 'China #2', age: '2 yr', height: '6–10″', price: 25, note: 'Seed-grown, vigorous.' },
  { name: 'Unknown', age: '2 yr', height: '8-16″', price: 10, note: 'Mystery tea. Exciting!' },
];

// ── Nav links ───────────────────────────────────────────────────

const NAV_LINKS = [
  { id: 'about', label: 'The farm' },
  { id: 'plants', label: 'Plants' },
  { id: 'community', label: 'Community' },
  { id: 'visit', label: 'Visit' },
];

// ── Main component ──────────────────────────────────────────────

export default function SiteA({ tweaks }) {
  const t = tweaks || {};
  const palette = t.paletteA || ['#3a4a39', '#2d3a2c', '#f4f1e8', '#7a8470'];
  const [moss, deepMoss, bone, sage] = palette;
  const ink = '#1f231d';
  const hero = t.heroA || 'illust';
  const density = t.density || 'cards';
  const fontHead = t.fontHeadA || "'Cormorant Garamond', Georgia, serif";
  const fontBody = t.fontBodyA || "'Inter', system-ui, sans-serif";
  const [menuOpen, setMenuOpen] = React.useState(false);

  const goTo = (id) => {
    setMenuOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const PAD_X = 'clamp(22px, 4vw, 64px)';

  const styles = {
    page: {
      width: '100%',
      minHeight: '100%',
      background: bone,
      color: ink,
      fontFamily: fontBody,
      fontSize: 16,
      lineHeight: 1.6,
      WebkitFontSmoothing: 'antialiased',
      '--bone': bone,
    },
    nav: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: `18px ${PAD_X} 10px`,
      position: 'sticky',
      top: 0,
      background: bone + 'ee',
      backdropFilter: 'blur(6px)',
      zIndex: 5,
    },
    wordmark: {
      fontFamily: fontHead,
      fontStyle: 'italic',
      fontSize: 18,
      fontWeight: 500,
      letterSpacing: 0.2,
      color: deepMoss,
      whiteSpace: 'nowrap',
    },
    menuBtn: {
      width: 30,
      height: 22,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      cursor: 'pointer',
      background: 'transparent',
      border: 'none',
      padding: 0,
    },
    menuLine: {
      height: 1,
      background: ink,
      width: '100%',
      transition: 'transform .2s, opacity .2s',
    },
    h1: {
      fontFamily: fontHead,
      fontWeight: 400,
      fontSize: 'clamp(48px, 7vw, 96px)',
      lineHeight: 1.02,
      letterSpacing: -0.5,
      color: deepMoss,
      margin: '0 0 18px',
      maxWidth: '14ch',
    },
    eyebrow: {
      fontSize: 11,
      letterSpacing: 2.4,
      textTransform: 'uppercase',
      color: sage,
      fontWeight: 500,
      marginBottom: 22,
    },
    lede: {
      fontFamily: fontHead,
      fontSize: 'clamp(20px, 2vw, 26px)',
      lineHeight: 1.35,
      fontWeight: 400,
      color: ink,
      marginBottom: 28,
      fontStyle: 'italic',
      maxWidth: '38ch',
    },
    section: { padding: `clamp(48px, 6vw, 88px) ${PAD_X}` },
    sectionTitle: {
      fontFamily: fontHead,
      fontWeight: 400,
      fontSize: 'clamp(34px, 4vw, 56px)',
      color: deepMoss,
      letterSpacing: -0.3,
      margin: '0 0 8px',
      maxWidth: '20ch',
    },
    sectionKicker: {
      fontSize: 11,
      letterSpacing: 2.4,
      textTransform: 'uppercase',
      color: sage,
      fontWeight: 500,
      marginBottom: 10,
    },
    rule: {
      border: 'none',
      borderTop: '1px solid ' + sage + '55',
      margin: `0 ${PAD_X}`,
    },
    sectionBody: { maxWidth: '60ch' },
  };

  // ── Hero variants ──────────────────────────────────────────────

  const HeroPhoto = (
    <section style={{ padding: `8px ${PAD_X} 36px` }}>
      <div style={styles.eyebrow}>Est. 2020 · Multnomah Co., OR</div>
      <h1 style={styles.h1}>Tea, grown in Oregon rain.</h1>
      <p style={styles.lede}>
        A small nursery raising <em>Camellia&nbsp;sinensis</em> for Pacific&nbsp;Northwest gardens
        &amp; farms.
      </p>
      <PlaceholderImage label="hero · tea field at dawn" h={360} bone={bone} sage={sage} />
    </section>
  );

  const HeroIllustration = (
    <section style={{ padding: `8px ${PAD_X} 36px` }}>
      <div style={styles.eyebrow}>Est. 2020 · Multnomah Co., OR</div>
      <h1 style={styles.h1}>Tea, grown in Oregon rain.</h1>
      <div
        style={{
          height: 'clamp(280px, 36vw, 460px)',
          margin: `24px calc(-1 * ${PAD_X})`,
          background: `radial-gradient(circle at 30% 60%, ${sage}55 0%, transparent 50%),
                     radial-gradient(circle at 70% 40%, ${moss}66 0%, transparent 55%),
                     ${bone}`,
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <BotanicalMark color={deepMoss} size={140} />
      </div>
      <p style={styles.lede}>
        A small nursery raising <em>Camellia&nbsp;sinensis</em> for Pacific&nbsp;Northwest gardens
        &amp; farms.
      </p>
    </section>
  );

  const HeroText = (
    <section style={{ padding: `24px ${PAD_X} 36px` }}>
      <div style={styles.eyebrow}>Est. 2020 · Multnomah Co., OR</div>
      <h1 style={{ ...styles.h1, fontSize: 'clamp(56px, 8vw, 120px)', lineHeight: 0.98 }}>
        Tea,
        <br />
        grown in
        <br />
        <em style={{ color: sage }}>Oregon rain.</em>
      </h1>
      <p style={styles.lede}>
        A small nursery raising <em>Camellia&nbsp;sinensis</em> — true tea — for
        Pacific&nbsp;Northwest gardens &amp; farms.
      </p>
      <div
        style={{
          fontFamily: fontHead,
          fontStyle: 'italic',
          fontSize: 15,
          color: sage,
          borderLeft: '1px solid ' + sage,
          paddingLeft: 14,
          marginTop: 28,
        }}
      >
        Eight cultivars, one through three years old, ready to set roots.
      </div>
    </section>
  );

  // ── Plant list variants ──────────────────────────────────────

  const PlantsCards = (
    <div
      style={{
        display: 'grid',
        gap: 18,
        gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
      }}
    >
      {PLANTS.map((p) => (
        <article
          key={p.name}
          data-testid="plant-card"
          style={{
            background: '#fff',
            border: '1px solid ' + sage + '40',
            padding: '20px 20px 22px',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              gap: 12,
            }}
          >
            <h3
              style={{
                fontFamily: fontHead,
                fontWeight: 500,
                fontSize: 26,
                color: deepMoss,
                margin: 0,
                letterSpacing: -0.2,
                whiteSpace: 'nowrap',
                minWidth: 0,
                flex: '0 1 auto',
              }}
            >
              {p.name}
            </h3>
            <div style={{ fontFamily: fontHead, fontSize: 22, color: ink, whiteSpace: 'nowrap' }}>
              ${p.price}
            </div>
          </div>
          <div
            style={{
              fontSize: 11,
              letterSpacing: 1.6,
              textTransform: 'uppercase',
              color: sage,
              marginTop: 6,
            }}
          >
            {p.age} · {p.height}
          </div>
          <p style={{ margin: '14px 0 0', color: ink + 'bb', fontSize: 14 }}>{p.note}</p>
        </article>
      ))}
    </div>
  );

  const PlantsList = (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
        columnGap: 40,
      }}
    >
      {PLANTS.map((p) => (
        <div
          key={p.name}
          data-testid="plant-list-item"
          style={{
            padding: '18px 0',
            borderTop: '1px solid ' + sage + '60',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              gap: 12,
            }}
          >
            <h3
              style={{
                fontFamily: fontHead,
                fontWeight: 500,
                fontSize: 24,
                color: deepMoss,
                margin: 0,
                whiteSpace: 'nowrap',
              }}
            >
              {p.name}
            </h3>
            <div style={{ fontFamily: fontHead, fontSize: 20, color: ink, whiteSpace: 'nowrap' }}>
              ${p.price}
            </div>
          </div>
          <div
            style={{
              fontSize: 11,
              letterSpacing: 1.6,
              textTransform: 'uppercase',
              color: sage,
              marginTop: 4,
            }}
          >
            {p.age} · {p.height} · {p.note}
          </div>
        </div>
      ))}
    </div>
  );

  const PlantsTable = (
    <table style={{ width: '100%', maxWidth: 720, borderCollapse: 'collapse', fontSize: 14 }}>
      <thead>
        <tr style={{ borderBottom: '1px solid ' + sage }}>
          <th
            style={{
              textAlign: 'left',
              padding: '10px 8px 10px 0',
              fontFamily: fontHead,
              fontWeight: 500,
              color: deepMoss,
            }}
          >
            Cultivar
          </th>
          <th
            style={{
              textAlign: 'left',
              padding: '10px 8px',
              fontFamily: fontHead,
              fontWeight: 500,
              color: deepMoss,
            }}
          >
            Age
          </th>
          <th
            style={{
              textAlign: 'right',
              padding: '10px 0 10px 8px',
              fontFamily: fontHead,
              fontWeight: 500,
              color: deepMoss,
            }}
          >
            Price
          </th>
        </tr>
      </thead>
      <tbody>
        {PLANTS.map((p) => (
          <tr
            key={p.name}
            data-testid="plant-row"
            style={{ borderBottom: '1px solid ' + sage + '40' }}
          >
            <td
              style={{
                padding: '14px 8px 14px 0',
                color: ink,
                fontWeight: 500,
                whiteSpace: 'nowrap',
              }}
            >
              {p.name}
            </td>
            <td style={{ padding: '14px 8px', color: sage, fontSize: 12, letterSpacing: 1 }}>
              {p.age}
            </td>
            <td
              style={{
                padding: '14px 0 14px 8px',
                textAlign: 'right',
                fontFamily: fontHead,
                fontSize: 18,
                color: ink,
              }}
            >
              ${p.price}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  // ── Render ────────────────────────────────────────────────────

  return (
    <div className="site" style={styles.page} data-testid="site-a">
      <nav style={styles.nav} role="navigation" aria-label="Main navigation">
        <div style={styles.wordmark}>Oregon Tea Growers</div>
        <button
          aria-label="Menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((o) => !o)}
          style={styles.menuBtn}
          data-testid="menu-button"
        >
          <span
            style={{
              ...styles.menuLine,
              transform: menuOpen ? 'translateY(10px) rotate(45deg)' : 'none',
            }}
          />
          <span style={{ ...styles.menuLine, opacity: menuOpen ? 0 : 1 }} />
          <span
            style={{
              ...styles.menuLine,
              transform: menuOpen ? 'translateY(-10px) rotate(-45deg)' : 'none',
            }}
          />
        </button>
        {menuOpen && (
          <div
            data-testid="nav-menu"
            style={{
              position: 'absolute',
              top: '100%',
              right: PAD_X,
              marginTop: 4,
              background: bone,
              border: '1px solid ' + sage + '55',
              boxShadow: '0 8px 24px rgba(0,0,0,.08)',
              padding: '8px 0',
              minWidth: 180,
            }}
          >
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                onClick={() => goTo(l.id)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '10px 18px',
                  border: 'none',
                  background: 'transparent',
                  fontFamily: fontHead,
                  fontStyle: 'italic',
                  fontSize: 18,
                  color: deepMoss,
                  cursor: 'pointer',
                }}
              >
                {l.label}
              </button>
            ))}
          </div>
        )}
      </nav>

      {hero === 'photo' && HeroPhoto}
      {hero === 'illust' && HeroIllustration}
      {hero === 'text' && HeroText}

      <hr style={styles.rule} />

      {/* About */}
      <section id="about" style={{ ...styles.section, scrollMarginTop: 64 }}>
        <div style={styles.sectionKicker}>The Farm</div>
        <h2 style={styles.sectionTitle}>
          Started small. <em style={{ color: sage }}>Still is.</em>
        </h2>
        <div style={styles.sectionBody}>
          <p style={{ marginTop: 18, color: ink + 'cc' }}>
            We grow tea on a small parcel in North Portland right next to Peninsula Park and the
            oldest public rose garden in the city. Giant douglas fir, cool nights, and rain enough
            that the tea doesn&apos;t ask for much. What started as a few seedlings is now a working
            nursery.
          </p>
          <p style={{ marginTop: 14, color: ink + 'cc' }}>
            Our plants are raised outdoors from year one, so they leave hardy. Cold tolerance to
            roughly USDA&nbsp;7a once established.
          </p>
        </div>
      </section>

      <hr style={styles.rule} />

      {/* Plants */}
      <section id="plants" style={{ ...styles.section, scrollMarginTop: 64 }}>
        <div style={styles.sectionKicker}>Available 2026</div>
        <h2 style={styles.sectionTitle}>Eight cultivars.</h2>
        <p style={{ marginTop: 14, marginBottom: 28, color: ink + 'cc' }}>
          One- to three-year-old nursery stock, container-grown. Limited quantities.
        </p>
        {density === 'cards' && PlantsCards}
        {density === 'list' && PlantsList}
        {density === 'table' && PlantsTable}
      </section>

      <hr style={styles.rule} />

      {/* Community */}
      <section id="community" style={{ ...styles.section, scrollMarginTop: 64 }}>
        <div style={styles.sectionKicker}>Community</div>
        <h2 style={styles.sectionTitle}>
          Tea gardens, <em style={{ color: sage }}>shared</em>.
        </h2>
        <div style={styles.sectionBody}>
          <p style={{ marginTop: 18, color: ink + 'cc' }}>
            We partner with community gardens around Portland to plant small tea plots — a few rows
            of <em>camellia sinensis</em> tucked between the tomatoes and the kale. Plants are
            donated, and we help with siting, soil prep and care.
          </p>
          <p style={{ marginTop: 14, color: ink + 'cc' }}>
            If your garden has a sunny corner with decent drainage, we&apos;d love to hear from you.
          </p>
        </div>
        <div
          style={{
            marginTop: 28,
            padding: '20px 22px',
            maxWidth: 480,
            border: '1px solid ' + sage + '55',
            background: '#ffffff80',
          }}
        >
          <div
            style={{
              fontSize: 11,
              letterSpacing: 2.4,
              textTransform: 'uppercase',
              color: sage,
              marginBottom: 8,
            }}
          >
            Currently planting with
          </div>
          <div style={{ fontFamily: fontHead, fontSize: 19, color: deepMoss, lineHeight: 1.5 }}>
            Loveleigh Community Garden
          </div>
        </div>
      </section>

      <hr style={styles.rule} />

      {/* Visit */}
      <section
        id="visit"
        style={{
          ...styles.section,
          background: deepMoss,
          color: bone,
          marginTop: 0,
          scrollMarginTop: 64,
        }}
      >
        <div style={{ ...styles.sectionKicker, color: sage }}>How it works</div>
        <h2 style={{ ...styles.sectionTitle, color: bone }}>
          Pickup at the <em>tea garden</em>,<br />
          by appointment.
        </h2>
        <div style={styles.sectionBody}>
          <p style={{ marginTop: 18, color: bone + 'cc' }}>
            Email us with your shortlist. We&apos;ll pick a Saturday or Sunday morning, send
            directions, and have your plants potted up and ready.
          </p>
          <p style={{ marginTop: 14, color: bone + 'cc' }}>
            No shipping. No drop-ins. We&apos;re a working farm, and we like it that way.
          </p>
        </div>
        <div
          style={{
            marginTop: 32,
            padding: '22px 20px',
            maxWidth: 480,
            border: '1px solid ' + sage,
            background: '#00000022',
          }}
        >
          <div
            style={{
              fontSize: 11,
              letterSpacing: 2.4,
              textTransform: 'uppercase',
              color: sage,
              marginBottom: 10,
            }}
          >
            Inquire
          </div>
          <div style={{ fontFamily: fontHead, fontSize: 22, color: bone, lineHeight: 1.4 }}>
            <a href="mailto:hello@oregonteagrowers.com" style={{ color: bone }}>
              hello@oregonteagrowers.com
            </a>
            <br />
            <a href="tel:+15033957395" style={{ color: sage }}>
              (503) 395&#8209;7395
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ padding: `36px ${PAD_X} 48px`, textAlign: 'center', color: sage }}>
        <BotanicalMark color={sage} size={36} />
        <div
          style={{
            fontFamily: fontHead,
            fontStyle: 'italic',
            fontSize: 16,
            marginTop: 12,
            color: deepMoss,
          }}
        >
          Oregon Tea Growers
        </div>
        <div
          style={{
            fontSize: 11,
            letterSpacing: 1.6,
            textTransform: 'uppercase',
            marginTop: 8,
          }}
        >
          Portland, OR · Pickup only
        </div>
      </footer>
    </div>
  );
}
